import { useCallback, useEffect, useRef, useState } from "react";
import { ArrowsClockwise, ListBullets, Password } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { logError } from "@/lib/log";

const KIND_STYLE = {
  info: "prefix-info",
  action: "prefix-info",
  "message-in": "prefix-info",
  "message-out": "prefix-info",
  click: "prefix-info",
  "fish-caught": "prefix-success",
  rare: "prefix-rare",
  gift: "prefix-gift",
  special: "prefix-gift",
  verification: "prefix-warn",
  error: "prefix-error",
  start: "prefix-success",
  stop: "prefix-warn",
  pause: "prefix-warn",
  resume: "prefix-info",
  sell: "prefix-success",
  extract: "prefix-success",
  favorite: "prefix-gift",
};

const LEVEL_LABEL = {
  info: "INFO",
  warn: "WARN",
  error: "ERR",
  success: "OK",
};

function formatTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleTimeString("id-ID", { hour12: false });
}

export default function Activity() {
  const [view, setView] = useState("activity");
  const [events, setEvents] = useState([]);
  const [otpMessages, setOtpMessages] = useState([]);
  const [otpError, setOtpError] = useState("");
  const [otpLoading, setOtpLoading] = useState(false);
  const [filter, setFilter] = useState("");
  const [autoScroll, setAutoScroll] = useState(true);
  const scrollRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/automation/events", { params: { limit: 200 } });
      setEvents((r.data.events || []).reverse());
    } catch (e) { logError("Gagal memuat activity log:", e); }
  }, []);

  const loadOtp = useCallback(async () => {
    setOtpLoading(true);
    try {
      const r = await api.get("/telegram/otp-messages", { params: { limit: 10 } });
      setOtpMessages(r.data.messages || []);
      setOtpError("");
    } catch (e) {
      setOtpMessages([]);
      setOtpError(e?.response?.data?.detail || "Pesan Telegram belum bisa dibaca");
    } finally {
      setOtpLoading(false);
    }
  }, []);

  useEffect(() => {
    if (view !== "activity") return undefined;
    load();
    const t = setInterval(load, 2500);
    return () => clearInterval(t);
  }, [load, view]);

  useEffect(() => {
    if (view !== "otp") return undefined;
    loadOtp();
    const t = setInterval(loadOtp, 10000);
    return () => clearInterval(t);
  }, [loadOtp, view]);

  useEffect(() => {
    if (autoScroll && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [events, autoScroll]);

  const filtered = filter
    ? events.filter(
        (e) =>
          e.kind === filter ||
          e.level === filter ||
          e.message.toLowerCase().includes(filter.toLowerCase())
      )
    : events;

  return (
    <div className="space-y-6" data-testid="activity-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-pink-500 mb-2">
            {view === "activity" ? "Log" : "Telegram"}
          </div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold">
            {view === "activity" ? "Activity Log" : "Kode OTP"}
          </h1>
          <p className="text-slate-400 mt-2">
            {view === "activity"
              ? "Real-time event stream dari automation engine (auto-refresh 2.5s)."
              : "Pesan resmi Telegram untuk akun yang sedang dipilih."}
          </p>
        </div>
        {view === "activity" ? <div className="flex gap-2 items-center flex-wrap">
          <input
            data-testid="filter-input"
            placeholder="Filter kind/level/text..."
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="bg-[#05050A] border border-white/10 rounded-md px-3 py-2 text-sm font-mono placeholder:text-slate-600 focus:border-pink-500 outline-none"
          />
          <Button
            onClick={() => setAutoScroll((v) => !v)}
            variant="outline"
            className={`rounded-md border-white/10 ${autoScroll ? "text-pink-400" : ""}`}
          >
            {autoScroll ? "Auto-scroll ON" : "Auto-scroll OFF"}
          </Button>
          <Button
            onClick={load}
            variant="outline"
            className="rounded-md border-white/10"
            data-testid="refresh-activity"
          >
            <ArrowsClockwise size={14} className="mr-2" /> Refresh
          </Button>
        </div> : (
          <Button
            onClick={loadOtp}
            disabled={otpLoading}
            variant="outline"
            className="rounded-md border-white/10"
            data-testid="refresh-otp"
          >
            <ArrowsClockwise size={14} className="mr-2" />
            {otpLoading ? "Memuat..." : "Refresh"}
          </Button>
        )}
      </div>

      <div className="inline-flex rounded-md border border-white/10 bg-[#08080F] p-1" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={view === "activity"}
          onClick={() => setView("activity")}
          className={`flex items-center gap-2 px-3 py-2 rounded text-sm ${view === "activity" ? "bg-pink-500 text-black" : "text-slate-400 hover:text-white"}`}
        >
          <ListBullets size={17} /> Activity Log
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={view === "otp"}
          onClick={() => setView("otp")}
          className={`flex items-center gap-2 px-3 py-2 rounded text-sm ${view === "otp" ? "bg-pink-500 text-black" : "text-slate-400 hover:text-white"}`}
          data-testid="otp-tab"
        >
          <Password size={17} /> Kode OTP
        </button>
      </div>

      {view === "activity" ? <div
        ref={scrollRef}
        className="terminal rounded-lg border border-white/10 p-4 h-[560px] overflow-y-auto"
        data-testid="activity-log"
      >
        {filtered.length === 0 && (
          <div className="text-slate-600 text-center py-16 font-mono text-sm">
            &gt; Belum ada aktivitas. Start automation untuk melihat log.
          </div>
        )}
        {filtered.map((e) => (
          <div key={e.id} className="flex gap-3 py-1 border-b border-white/[0.03]" data-testid="activity-row">
            <span className="text-slate-600 shrink-0">{formatTime(e.created_at)}</span>
            <span className={`shrink-0 uppercase font-semibold ${KIND_STYLE[e.kind] || "prefix-info"}`}>
              [{LEVEL_LABEL[e.level] || e.level || "INFO"}·{e.kind}]
            </span>
            <span className="text-slate-300 break-words min-w-0 flex-1">{e.message}</span>
          </div>
        ))}
      </div> : (
        <div className="border-t border-white/10" data-testid="otp-messages">
          {otpError && (
            <div role="alert" className="py-8 text-amber-300">{otpError}</div>
          )}
          {!otpError && !otpLoading && otpMessages.length === 0 && (
            <div className="py-12 text-slate-500">Belum ada pesan dari Telegram.</div>
          )}
          {otpMessages.map((message) => (
            <article key={message.id} className="py-5 border-b border-white/10">
              <time className="text-xs text-slate-500">
                {message.date ? new Date(message.date).toLocaleString("id-ID", { hour12: false }) : ""}
              </time>
              <div className="mt-2 whitespace-pre-wrap break-words font-mono text-sm text-slate-100 select-text">
                {message.text}
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}
